import PropTypes from 'prop-types';

import ArrowCircleLeftOutlinedIcon from '@mui/icons-material/ArrowCircleLeftOutlined';
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { Backdrop, Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import usePrevious from '../../hooks/usePrevious';

const styledImageProps = ['index', 'selectedIndex', 'prevIndex'];

const StyledImage = styled('img', {
  shouldForwardProp: (prop) => !styledImageProps.includes(prop),
})(({ index, selectedIndex, prevIndex }) => ({
  '@keyframes appear': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  '@keyframes disappear': {
    from: {
      opacity: 1,
    },
    to: {
      opacity: 0,
    },
  },
  ...(index === selectedIndex
    ? {
        width: '100%',
        height: '100%',
        objectFit: 'scale-down',
        objectPosition: '50% 50%',
        animation: `appear .3s ease`,
      }
    : index === prevIndex
    ? {
        // remove from normal document flow and center the image
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        top: '50%',
        left: '50%',
        // keep same image dimensions as in .image class
        width: '100%',
        height: '100%',
        objectFit: 'scale-down',
        // change opacity to 0
        animation: `disappear .3s ease`,
        // keep animation end result
        animationFillMode: 'forwards',
      }
    : { display: 'none' }),
}));

const StyledCloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: 10,
  right: 20,
  color: 'white',
  cursor: 'pointer',
  zIndex: 232,
}));

const StyledCaption = styled(Typography)(() => ({
  color: 'white',
  position: 'absolute',
  left: 20,
  bottom: 20,
  zIndex: 232,
}));

const StyledArrow = styled(IconButton)(() => ({
  color: 'white',
  position: 'absolute',
  zIndex: 232,
}));

const Carousel = ({
  images,
  selectedIndex,
  setSelectedIndex,
  isFullScreen,
  toggleFullScreen,
}) => {
  const prevIndex = usePrevious(selectedIndex);

  const handleNext = (e) => {
    e.stopPropagation();
    if (selectedIndex === images.length - 1) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleBack = (e) => {
    e.stopPropagation();
    if (selectedIndex === 0) {
      setSelectedIndex(images.length - 1);
    } else {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  return (
    <Backdrop
      open={isFullScreen}
      sx={{ zIndex: 10000, backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={(e) => toggleFullScreen(e, null)}
    >
      <StyledCloseButton onClick={(e) => toggleFullScreen(e, null)}>
        <HighlightOffOutlinedIcon fontSize="large" />
      </StyledCloseButton>
      {images.length > 1 && (
        <StyledArrow onClick={handleBack} sx={{ left: 20 }}>
          <ArrowCircleLeftOutlinedIcon fontSize="large" />
        </StyledArrow>
      )}
      <Box sx={{ width: '100%', height: '100vh' }}>
        {images?.map((image, i) => (
          <StyledImage
            key={i}
            index={i}
            prevIndex={prevIndex}
            selectedIndex={selectedIndex}
            alt={image?.caption}
            src={image?.source?.fullscreen}
          />
        ))}
      </Box>

      {images.length > 1 && (
        <StyledArrow onClick={handleNext} sx={{ right: 20 }}>
          <ArrowCircleRightOutlinedIcon fontSize="large" />
        </StyledArrow>
      )}
      <StyledCaption>{images[selectedIndex]?.caption}</StyledCaption>
    </Backdrop>
  );
};

Carousel.propTypes = {
  images: PropTypes.instanceOf(Array).isRequired,
  isFullScreen: PropTypes.bool.isRequired,
  toggleFullScreen: PropTypes.func.isRequired,
  setSelectedIndex: PropTypes.func.isRequired,
  selectedIndex: PropTypes.number,
};

export default Carousel;
